import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	ShoppingCart,
	Truck,
	RotateCcw,
	Minus,
	Plus,
	Share2,
	Loader2,
} from 'lucide-react'
import Layout from '@/components/layout/layout'
import ProductSlider from '@/components/sliders/products-slider'
import Toast from '@/components/elements/toast'
import {
	fetchProductById,
	getBestDeals,
} from '@/services/api/products/products-filter'
import {
	ProductEntity,
	AttributeProductEntity,
	NameAttributes,
} from '@/entities/api/products/product'
import ProductImageModal from '@/components/elements/product/image-modal'
import RatingStars from '@/components/elements/product/raithing-star'
import ProductRatingsSection from '@/components/elements/product/product-raithings'
import { API_URL } from '@/services/api/keys'
import ProductSkeleton from '@/components/elements/product/product-skeleton'
import ProductNotFound from '@/components/elements/product/product-notfound'
import ProductDetails from '@/components/elements/product/product-details'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import Guarantees from '@/components/layout/parts/guarantees'
import { ShareWithUrl } from '@/utils/share'
import { verifyStockOfAttribute } from '@/services/api/products/attributes'
import CartEntity from '@/entities/api/cart/cart'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { addToCart } from '@/store/features/cart'
import {
	getAttributeDisplayValue,
	normalizeAttribute,
} from '@/utils/attribute-display'
import { attributeNameConvert } from '@/utils/attributes-name-convert'
import CustomersEntity from '@/entities/api/customers/customer'
import { createCartProduct } from '@/services/api/cart/cart'
import { setOrdersFn } from '@/store/features/orders'
import { saveLastRoute } from '@/utils/save-lastroute'
import { Entities } from '@/entities/api/entities'

const Product = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const { product: productEncode, id: productId } = useParams()
	const productName = decodeURIComponent(productEncode ?? '').replace(/-/g, ' ')

	const [product, setProduct] = useState<ProductEntity | null>(null)
	const [products, setProducts] = useState<FilteredProductsResponse | null>()
	const [isLoading, setIsLoading] = useState(false)
	const [selectedAttributes, setSelectedAttributes] = useState<{
		[key: string]: AttributeProductEntity
	}>({})
	const [isProcessing, setIsProcessing] = useState(false)
	const [quantity, setQuantity] = useState(1)
	const [selectAttribute, setSelectAttribute] =
		useState<AttributeProductEntity | null>(null)
	const [imageModalOpen, setImageModalOpen] = useState(false)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const [showZoom, setShowZoom] = useState<boolean>(false)
	const [zoomPosition, setZoomPosition] = useState<{ x: number; y: number }>({
		x: 50,
		y: 50,
	})

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768)
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	if (!productId && !productName) {
		return null
	}

	const getAttributeGroupKey = (attribute: AttributeProductEntity): string => {
		try {
			const normalized = normalizeAttribute(attribute)
			if (!normalized) return 'unknown'

			const names = normalized.name
			return names.join('-')
		} catch (error) {
			return Array.isArray(attribute.name)
				? attribute.name.join('-')
				: String(attribute.name)
		}
	}

	const areAttributesEqual = (
		attr1: AttributeProductEntity,
		attr2: AttributeProductEntity
	): boolean => {
		try {
			const norm1 = normalizeAttribute(attr1)
			const norm2 = normalizeAttribute(attr2)

			if (!norm1 || !norm2) return false

			if (norm1.name.length !== norm2.name.length) return false
			if (norm1.value.length !== norm2.value.length) return false

			const namesEqual = norm1.name.every(
				(name: any, index: number) => name === norm2.name[index]
			)
			const valuesEqual = norm1.value.every(
				(value: any, index: number) =>
					String(value) === String(norm2.value[index])
			)

			return namesEqual && valuesEqual
		} catch (error) {
			return attr1._id === attr2._id
		}
	}

	useEffect(() => {
		const getPromotions = async () => {
			try {
				setIsLoading(true)

				const response = await getBestDeals(24)

				if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'No se pudo obtener el producto, inténtalo más tarde.',
					})
					setIsLoading(false)
					return
				}

				setProducts(response?.data)
				setIsLoading(false)
			} catch (error) {
				Toast({ type: 'error', message: 'Error al obtener los productos' })
				setIsLoading(false)
			}
		}

		getPromotions()
	}, [])

	useEffect(() => {
		const getProduct = async () => {
			try {
				setIsLoading(true)
				const response = await fetchProductById(productId ?? '')

				if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'No se pudo obtener el producto, inténtalo más tarde.',
					})
					setIsLoading(false)
					return
				}

				const productData = response?.data
				setProduct(productData)

				if (productData?.attributes) {
					const initialAttributes: { [key: string]: AttributeProductEntity } =
						{}

					const attributeGroups = productData.attributes.reduce(
						(
							groups: { [key: string]: AttributeProductEntity[] },
							attr: AttributeProductEntity
						) => {
							const groupKey = getAttributeGroupKey(attr)
							if (!groups[groupKey]) {
								groups[groupKey] = []
							}
							groups[groupKey].push(attr)
							return groups
						},
						{} as { [key: string]: AttributeProductEntity[] }
					)

					Object.entries(
						attributeGroups as Record<string, AttributeProductEntity[]>
					).forEach(([groupKey, attrs]) => {
						initialAttributes[groupKey] = attrs[0]
						setSelectAttribute(attrs[0])
					})

					setSelectedAttributes(initialAttributes)
				}

				setIsLoading(false)
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudo obtener el producto, inténtalo más tarde.',
				})
				setIsLoading(false)
			}
		}

		getProduct()
	}, [productId])

	const attributeGroups = useMemo(() => {
		if (!product?.attributes) return {}

		return product.attributes.reduce(
			(
				groups: { [key: string]: AttributeProductEntity[] },
				attr: AttributeProductEntity
			) => {
				const groupKey = getAttributeGroupKey(attr)
				if (!groups[groupKey]) {
					groups[groupKey] = []
				}
				groups[groupKey].push(attr)
				return groups
			},
			{} as { [key: string]: AttributeProductEntity[] }
		)
	}, [product?.attributes])

	const currentPrice = useMemo(() => {
		if (!product) return 0

		const selectedAttrs = Object.values(selectedAttributes)
		const basePrice =
			selectedAttrs.length > 0 ? selectedAttrs[0]?.price || 0 : 0
		const discount = product.discount || 0

		return basePrice * (1 - discount / 100)
	}, [product, selectedAttributes])

	const currentStock = useMemo(() => {
		const selectedAttrs = Object.values(selectedAttributes)
		return selectedAttrs.length > 0 ? selectedAttrs[0]?.quantity || 0 : 0
	}, [selectedAttributes])

	const currentImages = useMemo(() => {
		const selectedAttrs = Object.values(selectedAttributes)
		const attributeImage = selectedAttrs.find(
			(attr: AttributeProductEntity) => attr.image
		)?.image

		if (attributeImage) {
			return [attributeImage, ...(product?.images || [])]
		}

		return product?.images || []
	}, [product, selectedAttributes])

	// const currentProductImages = useMemo(() => {
	// 	return product?.images ? product.images : []
	// }, [product?.images])

	const averageRating = useMemo(() => {
		if (!product?.raitings?.length) return 0
		return (
			product.raitings.reduce((sum, rating) => sum + rating.rate, 0) /
			product.raitings.length
		)
	}, [product?.raitings])

	const handleAttributeSelect = (
		attributeGroupKey: string,
		option: AttributeProductEntity
	) => {
		setSelectedAttributes((prev) => ({
			...prev,
			[attributeGroupKey]: option,
		}))
		setSelectAttribute(option)
	}

	const verifyStock = async () => {
		if (!selectAttribute) {
			Toast({
				type: 'info',
				message: `Selecciona un producto para continuar`,
			})
			return
		}

		if (currentStock === 0) {
			Toast({
				type: 'info',
				message: 'Producto agotado',
			})
			return
		}

		const response = await verifyStockOfAttribute(
			productId ?? '',
			selectAttribute?._id ?? ''
		)

		if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
			Toast({
				type: 'error',
				message: 'El producto no se puede agregar al carrito',
			})
			setIsLoading(false)
			return
		}

		const { quantity: quantityFromApi } = response?.data

		if (quantity > currentStock || quantity > quantityFromApi) {
			Toast({
				type: 'info',
				message: `Solo quedan ${
					currentStock || quantityFromApi
				} unidades disponibles`,
			})
			return
		}
	}

	const handleAddToCart = async () => {
		try {
			await verifyStock()

			if (product?.type === Entities.PLANS_PERSONALIZED) {
				if (quantity > 5) {
					Toast({
						type: 'info',
						message: 'No se pueden comprar mas de 5 planes a la vez',
					})
					return
				}
			}

			const normalizedAttribute = normalizeAttribute(selectAttribute)
			if (!normalizedAttribute) {
				Toast({
					type: 'error',
					message: 'Error al procesar el atributo del producto',
				})
				return
			}

			const cart: CartEntity = {
				product: {
					...product,
					attributes: [{ ...normalizedAttribute, quantity }],
				},
				customer: customer
					? {
							_id: customer?._id,
							role: customer?.role,
							name: customer?.name,
							lastname: customer?.lastname,
							images: customer?.images ? customer?.images : [],
							email: customer?.email,
							phone: customer?.phone,
							_created: customer?._created,
					  }
					: {
							_id: uuidv4(),
							name: 'default',
							lastname: 'default',
					  },
				_created: new Date().toDateString(),
				_modified: new Date().toDateString(),
				_deleted: false,
			}

			if (!customer) {
				cart._id = uuidv4()
				cart.isLocal = true
				cart._created = new Date().toDateString()
				cart._modified = new Date().toDateString()
				cart._deleted = false
			}

			if (!!customer) {
				const body = {
					product: product?._id,
					attribute: normalizedAttribute._id,
					customer: customer?._id,
					quantity,
				}

				const response = await createCartProduct(body, token ?? '')

				if ([400, 401, 409, 404].includes(response.status ?? 0)) {
					Toast({
						type: 'error',
						message:
							'No se pudo agregar el producto al carrito, contacta con soporte',
					})
					return
				}

				dispatch(addToCart({ cartItem: response?.data?.data }))
			} else {
				dispatch(addToCart({ cartItem: cart }))
			}

			Toast({
				type: 'info',
				message: `${product?.name} x ${quantity} agregado al carrito correctamente.`,
			})
			setQuantity(1)
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo procesar la acción' })
		}
	}

	const handleProceedToCheckout = async () => {
		try {
			await verifyStock()
			const normalizedAttribute = normalizeAttribute(selectAttribute)
			if (!normalizedAttribute) {
				Toast({
					type: 'error',
					message: 'Error al procesar el atributo del producto',
				})
				return
			}
			setIsProcessing(true)
			setTimeout(() => {
				setIsProcessing(false)
				dispatch(
					setOrdersFn({
						items: [
							{
								_id: customer ? uuidv4() : undefined,
								product: {
									...product,
									attributes: [{ ...normalizedAttribute, quantity }],
								},
								customer: customer
									? {
											_id: customer?._id,
											role: customer?.role,
											name: customer?.name,
											lastname: customer?.lastname,
											images: customer?.images ? customer?.images : [],
											email: customer?.email,
											phone: customer?.phone,
											_created: customer?._created,
									  }
									: {
											_id: uuidv4(),
											name: 'default',
											lastname: 'default',
									  },
								isLocal: customer ? undefined : true,
								_created: new Date().toDateString(),
								_modified: new Date().toDateString(),
								_deleted: false,
							},
						],
					})
				)

				if (!customer) {
					saveLastRoute({ route: '/Pago', type: 'pay' })
					Toast({
						type: 'info',
						message: 'Inicia session para continuar con la compra',
					})
					navigate('/Login')
					return
				}

				navigate('/Pago')
			}, 1000)
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo procesar la acción' })
		}
	}

	if (isLoading) return <ProductSkeleton customer={customer} token={token} />

	if (!product) return <ProductNotFound customer={customer} token={token} />

	return (
		<Layout customer={customer} token={token}>
			<div className='w-full'>
				<div className='w-full bg-gradient-to-b from-gray-50 via-gray-50 to-white'>
					<div className='max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4'>
						<div className='rounded-xl bg-white shadow-md overflow-hidden'>
							<div className='grid grid-cols-1 lg:grid-cols-5 gap-0'>
								<div className='lg:col-span-2 relative h-full'>
									<div className='sticky top-4 w-full h-full'>
										<div className='flex flex-col h-full  rounded-l-xl lg:rounded-l-xl lg:rounded-r-none p-2 sm:p-3 lg:p-4'>
											<div
												className='relative flex-1 w-full mb-3 sm:mb-4 overflow-hidden rounded-xl shadow-sm border bg-white border-gray-100 group'
												onMouseMove={(e) => {
													if (!isMobile) {
														const rect = e.currentTarget.getBoundingClientRect()
														const x =
															((e.clientX - rect.left) / rect.width) * 100
														const y =
															((e.clientY - rect.top) / rect.height) * 100
														setZoomPosition({ x, y })
														setShowZoom(true)
													}
												}}
												onMouseLeave={() => {
													if (!isMobile) {
														setShowZoom(false)
													}
												}}
											>
												<div className='absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent z-[1]'></div>
												<img
													src={
														`${API_URL}/${currentImages[0]?.url}` ||
														'https://placehold.co/600x600'
													}
													alt={product.name}
													className='w-full h-full object-center object-cover cursor-pointer'
													onClick={() => {
														setCurrentImageIndex(0)
														setImageModalOpen(true)
													}}
													onMouseEnter={() =>
														!isMobile && setImageModalOpen(true)
													}
													onMouseLeave={() =>
														!isMobile && setImageModalOpen(false)
													}
												/>
												{product.discount > 0 && (
													<div className='absolute top-3 left-3 z-20'>
														<div className='bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-xl backdrop-blur-sm border border-red-500/20'>
															<span className='drop-shadow-sm'>
																-{product.discount}%
															</span>
														</div>
													</div>
												)}
												{currentStock <= 5 && currentStock > 0 && (
													<div className='absolute top-3 right-3 z-20'>
														<div className='bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-xl backdrop-blur-sm border border-orange-400/20'>
															<span className='drop-shadow-sm'>
																¡Últimas {currentStock}!
															</span>
														</div>
													</div>
												)}
												<div className='absolute bottom-3 right-3 z-20'>
													<div className='bg-black/20 backdrop-blur-md rounded-lg px-2 py-1 text-white text-xs font-medium'>
														{currentImageIndex + 1} / {currentImages.length}
													</div>
												</div>
												{!isMobile && showZoom && (
													<div
														className='absolute pointer-events-none border-2 border-white/50 bg-white/10 backdrop-blur-sm rounded-lg'
														style={{
															width: '100px',
															height: '100px',
															left: `${zoomPosition.x}%`,
															top: `${zoomPosition.y}%`,
															transform: 'translate(-50%, -50%)',
														}}
													/>
												)}
											</div>

											<div className='grid grid-cols-4 gap-2 sm:gap-2.5'>
												{currentImages.slice(0, 4).map((image, index) => (
													<button
														key={index}
														className={`cursor-pointer aspect-square relative overflow-hidden rounded-lg bg-white shadow-md transition-all duration-300 focus:outline-none ${
															index === currentImageIndex
																? 'ring-2 ring-red-600 shadow-lg scale-105 z-10'
																: 'ring-1 ring-gray-200 hover:ring-red-400 hover:shadow-lg hover:scale-102'
														}`}
														onClick={() => {
															setCurrentImageIndex(index)
															setImageModalOpen(true)
														}}
													>
														<div className='absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent'></div>
														<img
															src={
																`${API_URL}/${image.url}` ||
																'https://placehold.co/150x150'
															}
															alt={`${product.name} ${index + 1}`}
															className='w-full h-full object-center object-cover transition-transform duration-300 group-hover:scale-105'
														/>
														{index === currentImageIndex && (
															<div className='absolute inset-0 bg-red-600/10 ring-inset'></div>
														)}
														<div className='absolute bottom-1 right-1'>
															<div
																className={`w-2 h-2 rounded-full transition-all duration-300 ${
																	index === currentImageIndex
																		? 'bg-red-600 shadow-lg'
																		: 'bg-white/60'
																}`}
															></div>
														</div>
													</button>
												))}
											</div>

											{currentImages.length > 4 && (
												<div className='mt-2 text-center'>
													<button
														className='text-xs text-gray-500 hover:text-red-600 transition-colors duration-200 font-medium'
														onClick={() => setImageModalOpen(true)}
													>
														Ver todas las imágenes ({currentImages.length})
													</button>
												</div>
											)}
										</div>
									</div>
								</div>

								<div className='lg:col-span-3 p-4 sm:p-5 space-y-4 relative'>
									{!isMobile && showZoom && (
										<div
											className='absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[450px] z-50 bg-white rounded-lg overflow-hidden shadow-2xl border border-gray-200'
											style={{
												backgroundImage: `url(${API_URL}/${currentImages[0]?.url})`,
												backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
												backgroundSize: '300%',
												backgroundRepeat: 'no-repeat',
												willChange: 'background-position',
											}}
										>
											<div className='absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm'>
												Vista ampliada
											</div>
										</div>
									)}

									<div
										className={`space-y-2 ${
											!isMobile && showZoom ? 'mt-[370px]' : ''
										}`}
									>
										<div className='flex items-start justify-between gap-3'>
											<h1 className='text-xl lg:text-2xl font-bold text-gray-900 leading-tight'>
												{product.name}
											</h1>
											<div className='flex items-center gap-1 shrink-0'>
												<button
													onClick={ShareWithUrl}
													className='p-1.5 cursor-pointer rounded-full bg-gray-100 hover:bg-red-50 hover:text-red-600 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-red-500'
												>
													<Share2 size={16} />
												</button>
											</div>
										</div>

										<div className='flex items-center gap-2 md:p-2'>
											<RatingStars rating={Math.round(averageRating)} />
											<span className='text-xs text-gray-600'>
												({product.raitings?.length || 0} reseñas)
											</span>
										</div>

										<div className='flex items-center gap-2 md:pt-2'>
											<div
												className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
													currentStock > 0
														? 'bg-green-100 text-green-800'
														: 'bg-red-100 text-red-800'
												}`}
											>
												<div
													className={`w-1.5 h-1.5 rounded-full mr-1 ${
														currentStock > 0 ? 'bg-green-600' : 'bg-red-600'
													}`}
												></div>
												{currentStock > 0 ? 'En Stock' : 'Agotado'}
											</div>
											{currentStock > 0 && currentStock <= 10 && (
												<span className='text-xs text-orange-600 font-semibold'>
													Solo {currentStock} disponibles
												</span>
											)}
										</div>

										{selectAttribute && (
											<div className='flex items-center gap-2'>
												<span className='text-xs text-gray-600'>
													Variante seleccionada:
												</span>
												<span className='text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full font-medium'>
													{getAttributeDisplayValue(selectAttribute)}
												</span>
											</div>
										)}
									</div>

									<div className='flex items-baseline gap-2'>
										<div className='text-xl sm:text-2xl font-bold text-gray-900'>
											${currentPrice.toLocaleString('es-CO')}
										</div>
										{product.discount > 0 && (
											<div className='flex items-center gap-2'>
												<div className='text-sm text-gray-500 line-through'>
													$
													{(
														currentPrice /
														(1 - product.discount / 100)
													).toLocaleString('es-CO')}
												</div>
												<div className='bg-red-600 text-white px-1.5 py-0.5 rounded-md text-xs font-bold'>
													Ahorra $
													{(
														currentPrice / (1 - product.discount / 100) -
														currentPrice
													).toLocaleString('es-CO')}
													`
												</div>
											</div>
										)}
									</div>

									<div className='space-y-3 md:pb-12'>
										<div>
											<h3 className='text-sm font-semibold text-gray-900 mb-1'>
												Descripción
											</h3>
											<p className='text-xs text-gray-700 leading-relaxed'>
												{product.description?.length > 500
													? product.description.slice(0, 500) + '...'
													: product.description}
											</p>
										</div>
									</div>

									<div className='space-y-6 md:space-y-8 mt-6 md:mt-10 bg-gray-50 border border-gray-200 rounded-xl p-4 md:p-6'>
										{Object.entries(attributeGroups).map(
											([groupKey, options]) => {
												const firstOption = options[0]
												const normalized = normalizeAttribute(firstOption)
												const displayNames = normalized
													? normalized.name
															?.map((name: NameAttributes) =>
																attributeNameConvert(name)
															)
															.join(' + ')
													: groupKey

												return (
													<div
														key={groupKey}
														className='space-y-3 md:space-y-4'
													>
														<div className='flex items-center gap-2 md:gap-3'>
															<div className='w-7 h-7 md:w-8 md:h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0'>
																<svg
																	className='w-3.5 h-3.5 md:w-4 md:h-4 text-red-600'
																	fill='none'
																	stroke='currentColor'
																	viewBox='0 0 24 24'
																>
																	<path
																		strokeLinecap='round'
																		strokeLinejoin='round'
																		strokeWidth={2}
																		d='M9 5H7a2 2 0 00-2 2v8a2 2 0 002 2h2m0-12h6m-6 0v12m6-12h2a2 2 0 012 2v8a2 2 0 01-2 2h-2m-6 0h6'
																	/>
																</svg>
															</div>
															<h4 className='text-sm md:text-base font-semibold text-gray-900 capitalize'>
																Selecciona {displayNames}:
															</h4>
														</div>

														<div className='flex flex-wrap gap-1.5 md:gap-2'>
															{options.map((option, index) => {
																const isSelected =
																	selectedAttributes[groupKey] &&
																	areAttributesEqual(
																		selectedAttributes[groupKey],
																		option
																	)
																const displayValue =
																	getAttributeDisplayValue(option)
																const isLongText = displayValue.length > 12

																return (
																	<button
																		key={option._id || index}
																		onClick={() =>
																			handleAttributeSelect(groupKey, option)
																		}
																		className={`
										group relative rounded-lg border-2 px-2 py-1.5 md:px-3 md:py-2 transition-all duration-200
										flex items-center justify-center text-center min-w-fit
										hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-400
										${
											isSelected
												? 'border-red-500 bg-red-50 text-red-700 font-semibold shadow-sm ring-1 ring-red-200'
												: 'border-gray-300 bg-white hover:border-red-400 hover:bg-red-50 text-gray-700'
										}
										${
											option.quantity === 0
												? 'opacity-40 cursor-not-allowed'
												: 'cursor-pointer hover:scale-105 active:scale-95'
										}
									`}
																		disabled={option.quantity === 0}
																		title={displayValue}
																	>
																		<div className='w-full'>
																			<div
																				className={`font-medium ${
																					isLongText
																						? 'text-[10px] md:text-xs leading-tight'
																						: 'text-xs md:text-sm'
																				}`}
																			>
																				{isLongText ? (
																					<span className='block break-words max-w-[60px] md:max-w-none'>
																						{displayValue}
																					</span>
																				) : (
																					<span className='block'>
																						{displayValue}
																					</span>
																				)}
																			</div>

																			{option.quantity <= 5 &&
																				option.quantity > 0 && (
																					<div className='mt-0.5 md:mt-1 text-orange-600 text-[10px] md:text-xs font-medium'>
																						{option.quantity}
																					</div>
																				)}
																		</div>

																		{isSelected && (
																			<div className='absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3.5 h-3.5 md:w-4 md:h-4 bg-red-600 rounded-full flex items-center justify-center shadow-sm'>
																				<svg
																					className='w-2 h-2 md:w-2.5 md:h-2.5 text-white'
																					fill='currentColor'
																					viewBox='0 0 20 20'
																				>
																					<path
																						fillRule='evenodd'
																						d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
																						clipRule='evenodd'
																					/>
																				</svg>
																			</div>
																		)}

																		{option.quantity === 0 && (
																			<div className='absolute inset-0 rounded-lg bg-gray-200/50 flex items-center justify-center'>
																				<span className='text-[10px] md:text-xs text-gray-500 font-medium transform -rotate-12'>
																					Agotado
																				</span>
																			</div>
																		)}
																	</button>
																)
															})}
														</div>
													</div>
												)
											}
										)}
									</div>
									<div className='space-y-6 md:pt-4'>
										<div className='flex items-center gap-4 mt-4 mb-4 md:mt-6 md:mb-6 lg:mt-8 lg:mb-8'>
											<label className='text-sm font-medium text-gray-700 min-w-fit'>
												Cantidad:
											</label>
											<div className='flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 focus-within:border-red-400 focus-within:bg-white transition-all duration-200'>
												<button
													type='button'
													className='p-2.5 cursor-pointer text-gray-500 hover:text-red-500 hover:bg-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed'
													onClick={() => setQuantity(Math.max(1, quantity - 1))}
													disabled={quantity <= 1}
												>
													<Minus size={16} />
												</button>
												<input
													type='number'
													min='1'
													max={currentStock}
													value={quantity}
													onChange={(e) =>
														setQuantity(
															Math.min(
																currentStock,
																Math.max(1, parseInt(e.target.value) || 1)
															)
														)
													}
													className='w-14 py-2 border-0 text-center text-gray-900 focus:ring-0 focus:outline-none font-medium text-sm bg-transparent'
												/>
												<button
													type='button'
													className='p-2.5 cursor-pointer text-gray-500 hover:text-red-500 hover:bg-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed'
													onClick={() =>
														setQuantity(Math.min(currentStock, quantity + 1))
													}
													disabled={quantity >= currentStock}
												>
													<Plus size={16} />
												</button>
											</div>
										</div>

										<div className='grid grid-cols-2 gap-4 mt-4 mb-4 md:mt-6 md:mb-6 lg:mt-24 lg:mb-8'>
											<button
												type='button'
												onClick={handleAddToCart}
												disabled={currentStock === 0}
												className={`flex cursor-pointer items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
													currentStock === 0
														? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
														: 'bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow focus:ring-red-500'
												}`}
											>
												<ShoppingCart size={16} className='mr-2' />
												{currentStock === 0
													? 'Agotado'
													: `Agregar - $${(
															currentPrice * quantity
													  ).toLocaleString('es-CO')}`}
											</button>
											<button
												type='button'
												onClick={handleProceedToCheckout}
												disabled={currentStock === 0}
												className={`flex cursor-pointer items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
													currentStock === 0
														? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
														: 'bg-gray-900 hover:bg-black text-white shadow-sm hover:shadow focus:ring-gray-500'
												}`}
											>
												<ShoppingCart size={16} className='mr-2' />
												{currentStock === 0 ? (
													'Agotado'
												) : isProcessing ? (
													<>
														<Loader2 className='w-5 h-5 animate-spin' />
														Procesando...
													</>
												) : (
													`Comprar ahora - $${(
														currentPrice * quantity
													).toLocaleString('es-CO')}`
												)}
											</button>
										</div>
									</div>

									<div className='grid grid-cols-2 gap-3 pt-4'>
										<div className='flex items-center p-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200'>
											<div className='p-1.5 bg-green-100 rounded-lg mr-2'>
												<Truck size={16} className='text-green-700' />
											</div>
											<div>
												<p className='font-semibold text-green-900 text-xs'>
													Envío Gratis por compras superiores a 120.000 COP
												</p>
												<p className='text-green-700 text-xs'>A todo el país</p>
											</div>
										</div>

										<div className='flex items-center p-2 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200'>
											<div className='p-1.5 bg-blue-100 rounded-lg mr-2'>
												<RotateCcw size={16} className='text-blue-700' />
											</div>
											<div>
												<p className='font-semibold text-blue-900 text-xs'>
													Devoluciones
												</p>
												<p className='text-blue-700 text-xs'>Hasta 7 días</p>
											</div>
										</div>
									</div>

									{imageModalOpen && (
										<ProductImageModal
											isOpen={imageModalOpen}
											onClose={() => setImageModalOpen(false)}
											images={currentImages}
											currentIndex={currentImageIndex}
											isMobile={isMobile}
										/>
									)}
								</div>
							</div>
						</div>

						{product && <ProductDetails product={product} />}

						{product.raitings && product.raitings.length > 0 && (
							<div className='mt-6'>
								<ProductRatingsSection ratings={product.raitings} />
							</div>
						)}
					</div>
				</div>

				<div className='w-full py-[4vh] px-[2vw] lg:px-[6vw]'>
					<ProductSlider
						title='También te puede interesar'
						category='Otros'
						filteredProducts={products as FilteredProductsResponse}
						haveZone={false}
						customerId={customer?._id || ''}
						customer={customer}
					/>
				</div>
				<Guarantees />
			</div>
		</Layout>
	)
}

export default Product
